import React, { useState, useEffect } from 'react';
import api from './api';

const Dashboard = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadStats = async () => {
            try {
                const res = await api.get('/dashboard');
                setData(res.data);
                setLoading(false);
            } catch (err) { setLoading(false); }
        };
        loadStats();
    }, []);

    const formatNum = (num) => Math.round(num || 0).toLocaleString('en-IN');

    if (loading) return <div className="text-center p-5 text-muted">Loading Business Intel...</div>;
    if (!data) return <div className="text-center p-5">Error Loading Data</div>;

    return (
        <div className="container mt-4 mb-5">
            <h4 className="fw-bold mb-4">Executive Dashboard</h4>

            {/* ROW 1: MAIN METRICS */}
            <div className="row g-3 mb-4">
                
                {/* STOCK VALUE */}
                <div className="col-md-3">
                    <div className="card text-white bg-primary h-100 shadow-sm border-0">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <small className="opacity-75 text-uppercase fw-bold">Current Stock</small>
                                    <h3 className="fw-bold mb-0">₹ {formatNum(data.stock.val)}</h3>
                                </div>
                                <div className="h2 opacity-50">💎</div>
                            </div>
                            <div className="mt-2 small opacity-75">
                                {data.stock.cts.toFixed(2)} Cts in {data.stock.count} Packets
                            </div>
                        </div>
                    </div>
                </div>

                {/* RECEIVABLES */}
                <div className="col-md-3">
                    <div className="card text-white bg-success h-100 shadow-sm border-0">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <small className="opacity-75 text-uppercase fw-bold">Receivables</small>
                                    <h3 className="fw-bold mb-0">₹ {formatNum(data.finance.receivable)}</h3>
                                </div>
                                <div className="h2 opacity-50">📥</div>
                            </div>
                            <div className="mt-2 small opacity-75">
                                Pending from {data.counts.buyers} Buyers
                            </div>
                        </div>
                    </div>
                </div>

                {/* PAYABLES */}
                <div className="col-md-3">
                    <div className="card text-dark bg-warning h-100 shadow-sm border-0">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <small className="opacity-75 text-uppercase fw-bold">Payables</small>
                                    <h3 className="fw-bold mb-0">₹ {formatNum(data.finance.payable)}</h3>
                                </div>
                                <div className="h2 opacity-50">📤</div>
                            </div>
                            <div className="mt-2 small opacity-75">
                                Pending to {data.counts.parties} Suppliers
                            </div>
                        </div>
                    </div>
                </div>

                {/* PROFIT */}
                <div className="col-md-3">
                    <div className="card text-white bg-dark h-100 shadow-sm border-0">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <small className="opacity-75 text-uppercase fw-bold">Total Profit</small>
                                    <h3 className="fw-bold mb-0">₹ {formatNum(data.performance.profit)}</h3>
                                </div>
                                <div className="h2 opacity-50">📈</div>
                            </div>
                            <div className="mt-2 small opacity-75">
                                Gross Margin on Sales
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ROW 2: DETAILED SECTIONS */}
            <div className="row g-3">
                
                {/* CASH FLOW SUMMARY */}
                <div className="col-md-6">
                    <div className="card shadow-sm h-100">
                        <div className="card-header bg-white fw-bold">Cash Flow Health</div>
                        <div className="card-body">
                            <div className="d-flex justify-content-between mb-3 border-bottom pb-2">
                                <span className="text-muted">Total Sales (Revenue)</span>
                                <span className="fw-bold text-success">+ ₹ {formatNum(data.performance.sales)}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-3 border-bottom pb-2">
                                <span className="text-muted">Total Stock Value (Asset)</span>
                                <span className="fw-bold text-primary">+ ₹ {formatNum(data.stock.val)}</span>
                            </div>
                            <div className="alert alert-secondary mb-0">
                                <div className="d-flex justify-content-between fw-bold">
                                    <span>Net Position:</span>
                                    <span>
                                        ₹ {formatNum((data.finance.receivable + data.stock.val) - data.finance.payable)}
                                    </span>
                                </div>
                                <small className="text-muted">(Stock + Receivable - Payable)</small>
                            </div>
                        </div>
                    </div>
                </div>

                {/* RECENT ACTIVITY */}
                <div className="col-md-6">
                    <div className="card shadow-sm h-100">
                        <div className="card-header bg-white fw-bold">Recent Activity</div>
                        <div className="list-group list-group-flush">
                            {data.activity.length === 0 ? <div className="p-3 text-muted small">No activity yet.</div> :
                             data.activity.map((act, idx) => (
                                <div key={idx} className="list-group-item d-flex justify-content-between align-items-center">
                                    <div>
                                        <span className={`badge me-2 ${act.type === 'Sale' ? 'bg-success' : 'bg-primary'}`}>
                                            {act.type}
                                        </span>
                                        <small className="text-dark">{act.desc}</small>
                                    </div>
                                    <small className="fw-bold text-muted">₹ {formatNum(act.val)}</small>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;